import { HttpClient, HttpContext, HttpContextToken, HttpHeaders } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpMethods, HttpResponseType } from 'src/app/models/enums';
import { httpHeaders, httpHeadersMultipart } from './RequestHeaders.module';

export const SILENT_ERROR_STATUSES = new HttpContextToken<number[]>(() => []);

export function silentErrorStatuses(...statuses: number[]): HttpContext {
  return new HttpContext().set(SILENT_ERROR_STATUSES, statuses);
}

@Injectable()
export class BaseHttpService {
  public httpHeaders = new HttpHeaders();
  private requests: Observable<unknown> = new Observable<unknown>();

  constructor(@Inject(HttpClient) private httpClient: HttpClient) {
    this.setHeaders();
  }

  public create(body: unknown, url: string, headers?: any[], opts?: any): Observable<unknown> {
    const httpHeaders = (headers ?? []).reduce(
      (acc, { key, value }) => acc.set(key, value),
      this.httpHeaders
    );

    const options = {
      headers: httpHeaders,
      ...opts,
    };
    this.requests = this.httpClient.post<unknown>(url, body, options);
    this.intercept(this.requests);
    return this.requests;
  }

  public update(body: any, url: string, responseType: 'json' | 'text' = 'json'): Observable<unknown> {
    if (responseType === 'text') {
      this.requests = this.httpClient.put(url, body, { headers: this.httpHeaders, responseType: 'text' });
    } else {
      this.requests = this.httpClient.put(url, body, { headers: this.httpHeaders, responseType: 'json'});
    }
    this.intercept(this.requests);
    return this.requests;
  }

  public updatePatch(body: any, url: string, headers?: any[], opts?: any): Observable<unknown> {
    const httpHeaders = (headers ?? []).reduce(
      (acc, { key, value }) => acc.set(key, value),
      this.httpHeaders
    );

    const options = {
      headers: httpHeaders,
      ...opts,
    };
    this.requests = this.httpClient.patch<unknown>(url, body, options);
    this.intercept(this.requests);
    return this.requests;
  }

  public read(url: string, context?: HttpContext): Observable<unknown> {
    this.requests = this.httpClient.get(url, { headers: this.httpHeaders, context });
    this.intercept(this.requests);
    return this.requests;
  }

  public readText(url: string): Observable<string> {
    this.requests = this.httpClient.get(url, { headers: this.httpHeaders, responseType: 'text' });
    this.intercept(this.requests);
    return this.requests as Observable<string>;
  }
  
  public readBlob(url: string): Observable<unknown> {
    this.requests = this.httpClient.get(url, { headers: this.httpHeaders, responseType: 'blob', observe: 'response' });
    this.intercept(this.requests);
    return this.requests;
  }

  public readZip(url: string): Observable<ArrayBuffer> {
    const req = this.httpClient.get(url, { headers: this.httpHeaders, responseType: 'arraybuffer' });
    this.intercept(req);
    return req;
  }

  public createText(body: unknown, url: string): Observable<string> {
    this.requests = this.httpClient.post(url, body, { headers: this.httpHeaders, responseType: 'text' });
    this.intercept(this.requests);
    return this.requests as Observable<string>;
  }

  public createMultipart(url: string, formData: FormData, responseType: 'json' | 'text' = 'json'): Observable<unknown> {
    if (responseType === 'text') {
      this.requests = this.httpClient.post(url, formData, { headers: httpHeadersMultipart, responseType: 'text' });
    } else {
      this.requests = this.httpClient.post(url, formData, { headers: httpHeadersMultipart, responseType: 'json' });
    }
    this.intercept(this.requests);
    return this.requests;
  }

  public list(url: string, params: any): Observable<unknown> {
    this.requests = this.httpClient.get(url, {
      params: params,
      headers: this.httpHeaders,
    });
    this.intercept(this.requests);
    return this.requests;
  }

  public delete(url: string, body: any = {}, responseType: 'json' | 'text' = 'json'): Observable<unknown> {
    if (responseType === 'text') {
      this.requests = this.httpClient.delete(url, { headers: this.httpHeaders, body, responseType: 'text' });
    } else {
      this.requests = this.httpClient.delete(url, { headers: this.httpHeaders, body, responseType: 'json' });
    }
    this.intercept(this.requests);
    return this.requests;
  }

  setHeaders() {
    this.httpHeaders = httpHeaders;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private intercept(observable: Observable<unknown>) {
    this.setHeaders();
  }
}
